<template>
  <div>
    <ts-card>
      <ts-card-wrapper>
        <div class="ts-flex ts-justify-between">
          <div class="ts-flex ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <a-range-picker v-model="estimateDate" format="DD-MM-YYYY" valueFormat="YYYY-MM-DD" style="width: 200px;"
              :placeholder="['Estimate Start', 'Estimate End']" @change="fetchData()" />
          </div>
        </div>
      </ts-card-wrapper>
      <ts-table-filter :columns="columns" :data="resources" :loading="loading" @search="value => (search = value)">
        <template slot-scope="{ row }" slot="title">
          <a href="#" @click.prevent="onViewTask(row)" class="ts-mb-0 ts-no-underline hover:ts-no-underline">{{
            row.title }}</a>
        </template>
        <template slot-scope="{ row }" slot="status">
          <div class="ts-group">
            <select v-model="row.status_id"
              class="ts-py-1 ts-px-2 ts-rounded ts-outline-none group-hover:ts-hidden hide-select ts-w-32 ts-bg-transparent"
              readonly>
              <option v-for="(s, index) in status" :key="index" :value="s.status_id">{{ s.project_status }}</option>
            </select>
            <div class="group-hover:ts-block ts-hidden">
              <select v-model="row.status_id" class="ts-py-1 ts-px-2 ts-rounded ts-outline-none ts-w-32 hide-select"
                @change="updateColumnValueWhenChange(row, 'status_id')">
                <option v-for="(s, index) in status" :key="index" :value="s.status_id">{{ s.project_status }}</option>
              </select>
            </div>
          </div>
        </template>
        <template slot-scope="{ row }" slot="priority">
          <div class="ts-group">
            <div class="ts-flex ts-space-x-2 ts-items-center group-hover:ts-hidden">
              <div class="ts-h-2 ts-w-2 ts-rounded-full" :class="[priorities[row.priority_id].color]"></div>
              <span>{{ priorities[row.priority_id].label }}</span>
            </div>
            <div class="group-hover:ts-block ts-hidden">
              <select v-model="row.priority_id" class="ts-py-1 ts-px-2 ts-rounded ts-outline-none ts-w-32 hide-select"
                @change="updateColumnValueWhenChange(row, 'priority_id')">
                <option v-for="(p, id) in priorities" :key="id" :value="id">{{
                  p.label
                  }}</option>
              </select>
            </div>
          </div>
        </template>
        <template slot-scope="{ row }" slot="owner">
          <div class="ts-group">
            <span class="group-hover:ts-hidden">{{ row.owner_name }}</span>
            <div class="ts-relative group-hover:ts-block ts-hidden" style="z-index: 1024;">
              <div class="ts-absolute ts-top-0 ts--mt-4 ts-z-50">
                <div class="ts-fixed ts-bg-white ts-p-2 ts-rounded-md ts-border ts-border-blue-100" style="z-index: ">
                  <Select v-model="row.owner_id" :filterable="true" style="width:20rem" @on-change="
                    updateColumnValueWhenChange(row, 'owner_id', true)
                    ">
                    <Option v-for="item in owners" :value="item.user_id" :key="item.user_id">{{
                      item.employee_name_kh + ' ' + item.employee_name_en
                    }}</Option>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="estimated">
          <div @mouseover="onMouseover('show_index_est_date', index)">
            <div style="width: fit-content;" class="group-hover:ts-hidden ts-block"
              v-if="show_index_est_date !== index">
              <Icon type="ios-calendar-outline ts-mr-1" />
              <span>{{ row.estimate_start_date }}</span>
              <Icon type="ios-calendar-outline ts-mr-1 ts-ml-2" />
              <span>{{ row.estimate_end_date }}</span>
            </div>
            <a-range-picker v-if="index === show_index_est_date"
              :value="[row.estimate_start_date, row.estimate_end_date]" format="DD-MM-YYYY" valueFormat="DD-MM-YYYY"
              class="ts-w-full" @change="
                value =>
                  updateColumnValueWhenChange(
                    { ...value, ...row },
                    ['estimate_start_date', 'estimate_end_date'],
                    true
                  )
              " />
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="actual">
          <div @mouseover="onMouseover('show_index_act_date', index)">
            <div style="width: fit-content;" class="group-hover:ts-hidden ts-block"
              v-if="show_index_act_date !== index">
              <Icon type="ios-calendar-outline ts-mr-1" />
              <span>{{ row.actual_start_date }}</span>
              <Icon type="ios-calendar-outline ts-mr-1 ts-ml-2" />
              <span>{{ row.actual_end_date }}</span>
            </div>
            <a-range-picker v-if="index === show_index_act_date" :value="[row.actual_start_date, row.actual_end_date]"
              format="DD-MM-YYYY" valueFormat="DD-MM-YYYY" class="ts-w-full" @change="
                value =>
                  updateColumnValueWhenChange(
                    { ...value, ...row },
                    ['actual_start_date', 'actual_end_date'],
                    true
                  )
              " />
          </div>
        </template>
        <template slot-scope="{ row }" slot="remark">
          {{ limitString(row.remarks) }}
        </template>
        <template slot-scope="{ row }" slot="action">
          <div class="ts-flex ts-space-x-2" style="width: fit-content;">
            <a href="#" class="text-primary" @click.prevent="onEdit(row)" v-tooltip="$t('edit')">
              <Icon type="ios-create" size="20" />
            </a>
            <a href="#" class="text-danger" v-tooltip="$t('delete')" @click="deleteRecord(row)"
              :disabled="row._deleting">
              <i class="fas fa-circle-notch fa-spin" v-if="row._deleting"></i>
              <Icon type="ios-trash" size="20" v-else />
            </a>
          </div>
        </template>
      </ts-table-filter>
      <div class="ts-flex ts-items-center ts-justify-end ts-p-4">
        <ts-i-pagination v-model="pagination" @navigate="fetchData"></ts-i-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { camelCase, debounce, mapKeys } from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'

class Pagination {
  constructor(paging = {}) {
    this.currentPage = paging.currentPage ? parseInt(paging.currentPage) : 1
    this.lastPage = paging.lastPage ? parseInt(paging.lastPage) : 0
    this.perPage = paging.perPage ? parseInt(paging.perPage) : 15
    this.total = paging.total ? parseInt(paging.total) : 0
    this.from = this.perPage * (this.currentPage - 1) + 1
    this.to = this.perPage * this.currentPage
  }
}
export default {
  data() {
    return {
      loading: false,
      isOpen: false,
      show_index_est_date: undefined,
      show_index_act_date: undefined,
      pagination: new Pagination(),
      search: '',
      resources: [],
      status: [],
      owners: []
    }
  },
  computed: {
    limitString: () => (text, limit = 20) => {
      if (text != null) {
        let article = text.replace(/(<([^>]+)>)/gi, '')
        return article.length > limit
          ? article.substring(0, limit) + '...'
          : article
      }
      return ''
    },
    priorities() {
      return {
        1: {
          label: 'Low',
          color: 'ts-bg-gray-500'
        },
        2: {
          label: 'Normal',
          color: 'ts-bg-blue-500'
        },
        3: {
          label: 'Meduim',
          color: 'ts-bg-yellow-500'
        },
        4: {
          label: 'Hight',
          color: 'ts-bg-red-600'
        }
      }
    },
    columns() {
      return [
        {
          title: this.$t('task.title'),
          slot: 'title',
          keySearch: 'title',
          minWidth: 200
        },
        {
          title: this.$t('task.status'),
          slot: 'status',
          keySearch: 'status.project_status',
          minWidth: 120
        },
        {
          title: this.$t('task.priority'),
          slot: 'priority',
          keySearch: 'pri.label',
          minWidth: 120
        },
        {
          title: this.$t('task.owner'),
          slot: 'owner',
          keySearch: 'emp.employee_name_en',
          minWidth: 200
        },
        {
          title: this.$t('task.estimated'),
          slot: 'estimated',
          minWidth: 220
        },
        {
          title: this.$t('task.actual'),
          slot: 'actual',
          minWidth: 220
        },
        {
          title: this.$t('task.project'),
          key: 'project_name',
          keySearch: 'project_name',
          minWidth: 200
        },
        {
          title: this.$t('task.milestone'),
          key: 'milestone_name',
          keySearch: 'milestone_name',
          minWidth: 200
        },
        {
          title: this.$t('task.createdAt'),
          key: 'created_at',
          minWidth: 200
        },
        {
          title: this.$t('task.createdBy'),
          key: 'created_by_name',
          keySearch: 'empc.employee_name_en',
          minWidth: 120
        },
        {
          title: this.$t('task.remark'),
          slot: 'remark',
          minWidth: 200
        },
        {
          title: this.$t('actions'),
          slot: 'action',
          width: 150
        }
      ]
    },
    estimateDate: {
      get() {
        return this.$store.state.projectMgt.projectProfile.estimateDate
      },
      set(newValue) {
        this.$store.commit('projectMgt/projectProfile/SET_ESTIMATE_DATE', newValue)
      }
    },
  },
  created() {
    this.fetchData()
    this.fetchModel()
  },
  methods: {
    ...mapActions('projectMgt/taskProfile', [
      'fetch',
      'destroy',
      'formViewModel',
      'updateColumnValue'
    ]),
    ...mapActions('projectMgt/projectProfile', ['getTask']),
    addNew() {
      this.$router.push({
        name: 'task-create',
        query: {
          project_id: this.$route.params.id
        }
      })
    },
    updateColumnValueWhenChange(row, columns, isNeedFetch = false) {
      let model = {}
      if (Array.isArray(columns)) {
        columns.forEach((el, index) => {
          model[el] = moment(row[index], 'DD-MM-YYYY').isValid()
            ? moment(row[index], 'DD-MM-YYYY').format('YYYY-MM-DD')
            : row[index]
        })
      } else {
        model[columns] = row[columns]
      }
      if (!Object.keys(model).length) return
      this.updateColumnValue({
        id: row.id,
        data: model
      })
        .then(response => {
          this.$toasted.success(response.message)
          if (isNeedFetch) {
            this.fetch()
          }
          this.show_index_est_date = undefined
          this.show_index_act_date = undefined
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    },
    fetchData(attributes) {
      this.loading = true
      let params = Object.assign(
        {},
        {
          filter: {
            search: this.search,
            project_id: this.$route.params.id
          }
        },
        attributes
      )
      this.getTask(params)
        .then(response => {
          this.receiveResource(response)
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    },
    fetchModel() {
      this.formViewModel({
        fnName: 'status,owner'
      })
        .then(response => {
          this.status = response.data.status
          this.owners = response.data.owner
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    onEdit(record) {
      this.$router.push({
        name: 'task-edit',
        params: {
          id: record.id
        },
        query: {
          project_id: this.$route.params.id
        }
      })
    },
    deleteRecord(record) {
      this.$confirm({
        title: this.$t('are_you_sure_to_delete'),
        okText: this.$t('yes'),
        okType: 'danger',
        cancelText: this.$t('no'),
        centered: true,
        onOk: () => {
          return this.destroy(record.id)
            .then(response => {
              this.$toasted.success(response.message)
              this.fetchData()
              return Promise.resolve(response)
            })
            .catch(error => {
              this.$toasted.error(error.message)
              return Promise.reject(error)
            })
        },
        onCancel() { }
      })
    },
    onViewTask(record) {
      this.$router.push({
        name: 'task-view',
        params: {
          id: record.id
        },
        query: {
          project_id: record.project_id
        }
      })
    },
    onMouseover(key, index) {
      this.show_index_act_date = undefined
      this.show_index_est_date = undefined
      this[key] = index
    },
    receiveResource(response) {
      this.resources = []
      response.data.forEach(resource => {
        this.resources.push(Object.assign(resource, { _deleting: false }))
      })
      if (response.hasOwnProperty('meta')) {
        this.pagination = new Pagination(
          mapKeys(response.meta, (v, k) => camelCase(k))
        )
      } else {
        let { ...meta } = response
        this.pagination = new Pagination(mapKeys(meta, (v, k) => camelCase(k)))
      }
    }
  },
  watch: {
    search: debounce(function () {
      this.fetchData()
    }, 500)
  }
}
</script>

<style scoped>
.hide-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  text-indent: 1px;
  text-overflow: '';
}
</style>
